'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _div;
var _excluded = ["children", "defaultSelected", "defaultOpen", "open", "onOpenChange"],
  _excluded2 = ["className", "placeholder", "size", "forcedValue", "onMaskSelection"],
  _excluded3 = ["children", "className"];
import * as React from 'react';
import ArrowIosDown from '@crehana/compass.icons.outline/ArrowIosDown';
import ArrowIosUp from '@crehana/compass.icons.outline/ArrowIosUp';
import Search from '@crehana/compass.icons.outline/Search';
import { Command, CommandEmpty, CommandInput } from "../_shadcn-native-components/command";
import { Popper, PopperContent, PopperTrigger } from "../Popper";
import { cn } from "../utils";
import { DropdownContext, useDropdownContext } from "./context";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Dropdown = function Dropdown(_ref) {
  var children = _ref.children,
    _ref$defaultSelected = _ref.defaultSelected,
    defaultSelected = _ref$defaultSelected === void 0 ? [] : _ref$defaultSelected,
    _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    controlledOpen = _ref.open,
    onControlledOpenChange = _ref.onOpenChange,
    popoverProps = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(defaultOpen),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    uncontrolledOpen = _React$useState2[0],
    setUncontrolledOpen = _React$useState2[1];
  var _React$useState3 = React.useState(Array.isArray(defaultSelected) ? defaultSelected.map(function (item) {
      return item.value;
    }) : [defaultSelected.value]),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    values = _React$useState4[0],
    setValues = _React$useState4[1];
  var _React$useState5 = React.useState(Array.isArray(defaultSelected) ? defaultSelected : [defaultSelected]),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    items = _React$useState6[0],
    setItems = _React$useState6[1];
  var selectedItems = React.useMemo(function () {
    var selected = [];
    values.forEach(function (value) {
      var item = items.find(function (item) {
        return item.value === value;
      });
      if (item) selected.push(item);
    });
    return selected;
  }, [values, items]);
  var open = controlledOpen !== null && controlledOpen !== void 0 ? controlledOpen : uncontrolledOpen;
  var setOpen = onControlledOpenChange !== null && onControlledOpenChange !== void 0 ? onControlledOpenChange : setUncontrolledOpen;
  return /*#__PURE__*/_jsx(DropdownContext.Provider, {
    value: {
      open: open,
      values: values,
      items: items,
      selectedItems: selectedItems,
      setOpen: setOpen,
      setValues: setValues,
      setItems: setItems
    },
    children: /*#__PURE__*/_jsx(Popper, _objectSpread(_objectSpread({
      open: uncontrolledOpen,
      onOpenChange: setUncontrolledOpen
    }, popoverProps), {}, {
      children: children
    }))
  });
};
var DropdownTrigger = PopperTrigger;
var DropdownValue = function DropdownValue(_ref2) {
  var className = _ref2.className,
    placeholder = _ref2.placeholder,
    _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? 'small' : _ref2$size,
    forcedValue = _ref2.forcedValue,
    onMaskSelection = _ref2.onMaskSelection,
    popoverTriggerProps = _objectWithoutProperties(_ref2, _excluded2);
  var _useDropdownContext = useDropdownContext(),
    selectedItems = _useDropdownContext.selectedItems,
    open = _useDropdownContext.open;
  var selectedValues = selectedItems.map(function (item) {
    return item.label;
  }).join(', ');
  var showPlaceholder = selectedValues.length === 0;
  var translatedSelectedValues = (onMaskSelection === null || onMaskSelection === void 0 ? void 0 : onMaskSelection(selectedValues)) || selectedValues;
  return /*#__PURE__*/_jsx(DropdownTrigger, _objectSpread(_objectSpread({
    asChild: true,
    className: cn('tw-flex tw-items-center', size === 'small' ? 'tw-h-4 tw-font-body4' : 'tw-h-5.6 tw-font-body3', className)
  }, popoverTriggerProps), {}, {
    children: /*#__PURE__*/_jsxs("button", {
      className: "tw-input",
      role: "combobox",
      "aria-expanded": open,
      children: [/*#__PURE__*/_jsx("span", {
        className: cn('tw-grow tw-text-left tw-max-w-full tw-overflow-hidden tw-text-ellipsis tw-line-clamp-1', showPlaceholder ? 'tw-text-neutral-light-700 dark:tw-text-neutral-dark-500' : 'tw-text-neutral-light-800 dark:tw-text-neutral-light-100', popoverTriggerProps.disabled && 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600'),
        children: showPlaceholder ? placeholder : forcedValue !== null && forcedValue !== void 0 ? forcedValue : translatedSelectedValues
      }), open ? /*#__PURE__*/_jsx(ArrowIosUp, {
        size: 24,
        className: cn(popoverTriggerProps.disabled ? 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600' : 'tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500', 'group-data-[state=closed]:tw-hidden')
      }) : /*#__PURE__*/_jsx(ArrowIosDown, {
        size: 24,
        className: cn(popoverTriggerProps.disabled ? 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600' : 'tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500', 'group-data-[state=open]:tw-hidden')
      })]
    })
  }));
};
var DropdownContent = function DropdownContent(_ref3) {
  var children = _ref3.children,
    className = _ref3.className,
    otherProps = _objectWithoutProperties(_ref3, _excluded3);
  return /*#__PURE__*/_jsx(PopperContent, _objectSpread(_objectSpread({
    className: cn('tw-rounded tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700 dark:tw-shadow-none tw-m-0 tw-overflow-hidden', className),
    sideOffset: 14,
    align: "start",
    removePadding: true
  }, otherProps), {}, {
    children: /*#__PURE__*/_jsx(Command, {
      className: "tw-rounded",
      children: children
    })
  }));
};
var DropdownSearchInput = CommandInput;
var DropdownClearButton = function DropdownClearButton(_ref4) {
  var label = _ref4.label,
    className = _ref4.className,
    _ref4$unstyled = _ref4.unstyled,
    unstyled = _ref4$unstyled === void 0 ? false : _ref4$unstyled;
  var _useDropdownContext2 = useDropdownContext(),
    setValues = _useDropdownContext2.setValues;
  var handleClearAll = function handleClearAll() {
    return setValues([]);
  };
  return /*#__PURE__*/_jsx("button", {
    type: "button",
    onClick: handleClearAll,
    className: cn(!unstyled && 'tw-w-full tw-text-left tw-py-1.2 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500 hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600', className),
    id: "clear-all",
    children: label
  });
};
var DropdownEmpty = function DropdownEmpty(_ref5) {
  var title = _ref5.title,
    subtitle = _ref5.subtitle;
  return /*#__PURE__*/_jsxs(CommandEmpty, {
    className: "tw-flex tw-flex-col tw-items-center tw-justify-center",
    children: [_div || (_div = /*#__PURE__*/_jsx("div", {
      className: "tw-rounded-full dark:tw-bg-neutral-dark-600 tw-bg-neutral-light-300 tw-flex tw-items-center tw-justify-center tw-h-[60px] tw-w-[60px] tw-mb-0.8",
      children: /*#__PURE__*/_jsx(Search, {
        size: 24,
        className: "tw-text-neutral-light-600 dark:tw-text-neutral-dark-400"
      })
    })), /*#__PURE__*/_jsx("div", {
      className: "tw-font-link1 dark:tw-text-neutral-light-100",
      children: title
    }), subtitle && /*#__PURE__*/_jsx("p", {
      className: "tw-mt-0.4 tw-font-body5 tw-text-neutral-light-700 dark:tw-text-neutral-dark-400",
      children: subtitle
    })]
  });
};
export { Dropdown, DropdownClearButton, DropdownContent, DropdownEmpty, DropdownSearchInput, DropdownTrigger, DropdownValue };